import {Component, Input, OnChanges} from '@angular/core';
import {PerformanceComparisonMetric, PerformanceMapResponse, PerformanceMapType} from '../../my-brand-screen/types';
import Highcharts from 'highcharts/highmaps';
import {HighchartsChartModule} from 'highcharts-angular';
import {NoWidgetDataComponent} from '../../my-brand-screen/no-widget-data/no-widget-data.component';
import {NgIf} from '@angular/common';
import usaMap from '@highcharts/map-collection/countries/us/us-all.geo.json';
import worldMap from '@highcharts/map-collection/custom/world.geo.json';
import {SelectItem} from '../../../shared/components/select-menu/select-menu.component';
import {LoaderComponent} from '../../../shared/components/loader/loader.component';
import {ShortDisplayValuePipe} from '../../../pipes/short-display-value.pipe';

@Component({
  selector: 'app-competition-performance-map',
  imports: [
    HighchartsChartModule,
    NoWidgetDataComponent,
    NgIf,
    LoaderComponent
  ],
  providers: [
    ShortDisplayValuePipe
  ],
  templateUrl: './competition-performance-map.component.html',
  styleUrl: './competition-performance-map.component.scss'
})
export class CompetitionPerformanceMapComponent implements OnChanges {
  @Input() type?: PerformanceMapType;
  @Input() data?: PerformanceMapResponse;
  @Input() metric?: SelectItem;
  @Input() brandColors: Record<string, string> = {};
  @Input() loading = false;


  Highcharts: typeof Highcharts = Highcharts;
  chartOptions: Highcharts.Options = {};
  usPrefix = 'us-';
  myBrand: string | undefined;
  currColorIndex = 0;
  groupedCompetitorsImpressions: Record<string, number> = {};

  constructor(
    private shortDisplayValuePipe: ShortDisplayValuePipe
  ){}

  private getChartData(): [string, number | null][] {
    const groupedData = this.getCompetitorsGroupedData();
    const myBrandData = this.getMyBrandDataForMap();
    const map = this.type === PerformanceMapType.US ? usaMap : worldMap;
    return map.features.map((f: any) => {
      const code = f.properties['hc-key'];
      // symmetric relative difference calculation
      const calcValue = (((myBrandData[code] - (groupedData[code] / this.groupedCompetitorsImpressions[code])) / ((groupedData[code] / this.groupedCompetitorsImpressions[code] + myBrandData[code]) / 2)) * 100);
      return [
        code,
        isNaN(calcValue) ? null : calcValue
      ];
    });
  }

  private getCompetitorsGroupedData(): Record<string, number> {
    return (this.data?.myCompetitors ?? []).reduce((groupedData, item) => {
      const geoCode = this.getGeoCodeByType(item.geoCode);
      const metricKey = this.metric?.key as keyof typeof item;
      const rawValue = typeof item[metricKey] === 'number' ? item[metricKey] as number : 0;
      groupedData[geoCode] ||= 0;
      this.groupedCompetitorsImpressions[geoCode] ||= 0;
      this.groupedCompetitorsImpressions[geoCode] += (item.impressions || 0);
      groupedData[geoCode] += rawValue * (item.impressions || 0);
      return groupedData;
    }, {} as Record<string, number>);
  }

  private getMyBrandDataForMap(): Record<string, number> {
    return (this.data?.myBrand ?? []).reduce((brandData, item) => {
      const geoCode = this.getGeoCodeByType(item.geoCode);
      const metricKey = this.metric?.key as keyof typeof item;
      const rawValue = typeof item[metricKey] === 'number' ? item[metricKey] as number : 0;
      brandData[geoCode] ||= 0;
      brandData[geoCode] += rawValue;
      return brandData;
    }, {} as Record<string, number>);
  }

  private getCompetitorsImpressionsData(): Record<string, Record<string, number>> {
    const impressionsData: Record<string, Record<string, number>> = {};
    (this.data?.myCompetitors ?? []).forEach((item) => {
      const code = this.getGeoCodeByType(item.geoCode);
      impressionsData[code] ??= {};
      impressionsData[code][item.brand] = item['impressions'] ?? 0;
    });
    return impressionsData;
  }

  private getMyBrandImpressionsData(): Record<string, number> {
    const impressionsData: Record<string, number> = {};
    (this.data?.myBrand ?? []).forEach((item) => {
      this.myBrand ??= item.brand;
      const code = this.getGeoCodeByType(item.geoCode);
      impressionsData[code] = item['impressions'] ?? 0;
    });
    return impressionsData;
  }

  private getCompetitorsMetricData(): Record<string, Record<string, number>> {
    const metricData: Record<string, Record<string, number>> = {};
    (this.data?.myCompetitors ?? []).forEach((item) => {
      const metricKey = this.metric?.key as keyof typeof item;
      const code = this.getGeoCodeByType(item.geoCode);
      metricData[code] ??= {};
      metricData[code][item.brand] = typeof item[metricKey] === 'number' ? item[metricKey] as number : 0;
    });
    return metricData;
  }

  private getMyBrandMetricData(): Record<string, number> {
    const metricData: Record<string, number> = {};
    (this.data?.myBrand ?? []).forEach((item) => {
      const metricKey = this.metric?.key as keyof typeof item;
      const code = this.getGeoCodeByType(item.geoCode);
      metricData[code] = typeof item[metricKey] === 'number' ? item[metricKey] as number : 0;
    });
    return metricData;
  }

  private getGeoCodeByType(geoCode: string): string {
    return this.type === PerformanceMapType.US ? `${this.usPrefix}${geoCode}` : geoCode;
  }

  private getTooltipHtml(country: string, brandImp: number, brandMetric: number, competitorsImp: Record<string, number>, competitorsMetric: Record<string, number>): string {
    let content = '';
    if (!brandImp || !brandMetric) {
      content = `<div>No data found for ${country}</div>`;
    } else {
      content = `
      <div class="tooltip-country" style="font-weight: 700;">${country}</div>
      <div class="tooltip-metric" style="font-weight: 400;display: grid;grid-template-columns: auto auto 1fr auto;align-items: center;gap:7px;">
        <span class="color-circle" style="display:inline-block; width:8px; height:8px; border-radius:50%; background-color: ${this.brandColors[this.myBrand as string]}; margin-right:4px;"></span>
        <span class="tooltip-label">${this.myBrand}</span>
        <span class="impression-label" style="color:#828892">(${this.shortDisplayValuePipe.transform(brandImp)})</span>
        <span class="tooltip-value" style="font-weight: 600;text-align: right;">${this.getFormattedMetric(brandMetric)}</span>
      </div>
      <div class="tooltip-divider" style="border-top: 1px solid #DEE0E4"></div>
    `;
      Object.entries(competitorsImp).forEach(([compName, compImp]) => {
        content += `
      <div class="tooltip-metric" style="font-weight: 400;display: grid;grid-template-columns: auto auto 1fr auto;align-items: center;gap:7px;">
        <span class="color-circle" style="display:inline-block; width:8px; height:8px; border-radius:50%; background-color: ${this.brandColors[compName]}; margin-right:4px;"></span>
        <span class="tooltip-label">${compName}</span>
        <span class="impression-label" style="color:#828892">(${this.shortDisplayValuePipe.transform(compImp)})</span>
        <span class="tooltip-value" style="font-weight: 600;">${this.getFormattedMetric(competitorsMetric[compName])}</span>
      </div>
      `;
      });
    }
    return `
      <div class="tooltip-content"
        style="
          display: flex;
          flex-direction: column;
          padding: 12px;
          gap: 8px;
          color: #34363A;
          line-height: 18px;
          font-style: normal;
          font-family: Poppins;
          font-size: 12px;
          background: #FFFFFF;
          border-radius: 4px;
          box-shadow: 0px 0px 8px 2px rgba(0, 0, 0, 0.15);
        ">
      ${content}
      </div>
    `;

  }

  private getFormattedMetric(value: number, fixed: boolean = true): string {
    switch (this.metric?.key) {
      case PerformanceComparisonMetric.ATTENTION:
        return `${value}%`;
      case PerformanceComparisonMetric.DWELL_TIME:
        return `${value}s`;
      case PerformanceComparisonMetric.REFRESH_RATE:
        return `${value}s`;
      case PerformanceComparisonMetric.CPM:
        return fixed ? `$${value.toFixed(2)}` : `$${value}`;
      case PerformanceComparisonMetric.IMPRESSIONS:
        return `${this.shortDisplayValuePipe.transform(value, fixed)}`;
      case PerformanceComparisonMetric.AD_CLUTTER:
        return `${value.toFixed(1)}`;
      case PerformanceComparisonMetric.SPEND:
        return `${this.shortDisplayValuePipe.transform(value, fixed, 1000, '$')}`;
      default:
        return `${value}`;
    }
  }

  private getMinMaxValue(data: [string, number | null][]): { minValue: number, maxValue: number } {
    const values = data.map(([_, num]) => num || 0);
    const minValue = Math.min(...values as number[], -100);
    const maxValue = Math.max(...values as number[], 100);
    return {minValue, maxValue};
  }

  ngOnChanges() {
    if (!this.data?.myBrand?.length) return;
    this.groupedCompetitorsImpressions = {};
    this.currColorIndex = 0;
    this.myBrand = undefined;
    const globalStyle = {fontFamily: 'Poppins', fontSize: '12px'};
    const map = this.type === PerformanceMapType.US ? usaMap : worldMap;
    const currentMetric = this.metric;
    const chartData = this.getChartData();
    const brandImpressionsData = this.getMyBrandImpressionsData();
    const competitorsImpressionsData = this.getCompetitorsImpressionsData();
    const brandMetricData = this.getMyBrandMetricData();
    const competitorsMetricData = this.getCompetitorsMetricData();
    const getTooltipHtml = this.getTooltipHtml.bind(this);
    const {minValue, maxValue} = this.getMinMaxValue(chartData);
    this.chartOptions = {
      chart: {
        map: map,
        animation: false,
        height: 502,
        width: 1132,
        marginTop: 0,
        events: {
          load: function () {
            const chart = this;
            const legendBox = chart.legend.group.getBBox();
            chart.renderer.text('Higher Than <br> Competitors', legendBox.x + legendBox.width + 110, legendBox.y + 480).css({
              color: '#333333',
              fontSize: '12px',
              fontFamily: 'Poppins'
            }).add();
            chart.renderer.text('Lower Than <br> Competitors', legendBox.x + 12, legendBox.y + 480).css({
              color: '#333333',
              fontSize: '12px',
              fontFamily: 'Poppins',
            }).add();
            chart.renderer.rect(legendBox.x + legendBox.width + 215, legendBox.y + 470, 40, 12, 3).attr({
                fill: '#BDC2CA',
              }).add();
            chart.renderer.text('No Data', legendBox.x + legendBox.width + 210, legendBox.y + 502).css({
                color: '#333333',
                fontSize: '12px',
                fontFamily: 'Poppins'
              }).add();
          }
        }
      },
      title: {
        text: undefined
      },
      credits: {
        enabled: false
      },
      mapNavigation: {
        enabled: false
      },
      legend: {
        layout: 'horizontal',
        align: 'left',
        verticalAlign: 'bottom',
        symbolWidth: 400,
        itemStyle: globalStyle,
        y: 24,
        x: 90
      },
      series: [
        {
          type: "map",
          name: currentMetric?.label,
          data: chartData,
          nullColor: '#BDC2CA',
          borderColor: '#FFFFFF',
          borderWidth: 1,
          states: {
            hover: {
              enabled: false
            }
          },
          dataLabels: {
            enabled: false
          },
        }
      ],
      colorAxis: {
        min: minValue,
        max: maxValue,
        tickAmount: 9,
        allowDecimals: false,
        stops: [
          [0, '#6D3EA2'],
          [0.125, '#4965A4'],
          [0.25, '#3081A6'],
          [0.375, '#169BA7'],
          [0.5, '#00B3A8'],
          [0.625, '#38BA97'],
          [0.75, '#72C083'],
          [0.875, '#ACC770'],
          [1, '#FBD057']
        ],
        labels: {
          padding: 0,
          align: "center",
          overflow: "allow",
          style: globalStyle,
          formatter: function (this: any) {
            return this.value + '%'
          }
        },
      },
      tooltip: {
        useHTML: true,
        shadow: false,
        backgroundColor: 'transparent',
        formatter: function (this: any) {
          const country = this.point?.name;
          const geoCode = this.point?.['hc-key'];
          return getTooltipHtml(country, brandImpressionsData[geoCode], brandMetricData[geoCode], competitorsImpressionsData[geoCode], competitorsMetricData[geoCode]);
        }
      },

    }
  }

}
